/**
 * Excluir BU, behind typing its slug.
 *
 * The exclusion is soft: nothing is erased, the BU and everything under it stop being reachable
 * and move to the Excluídas tab. The confirmation is still typed, because a BU is the root of the
 * whole tree — pages, tours, announcements, signatures and saved preferences all go dark with it.
 *
 * @module Pages/BUs/BUManager
 */

import { css } from '@emotion/css';
import { UTButton, UTModal, UTTextField } from '@group-link-one/gl-ui/utilities';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { getTenantCascadePreview } from '../../Services';
import type { Tenant } from '../BUPlatformList/BUPlatformList.types';
import { CascadeSummary } from '../CascadeSummary/CascadeSummary';

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
  width: 100%;
  min-width: 0;
`;

const textStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const noteStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border-radius: var(--radius-m);
  background: var(--color-warning-subtle);
  border: 1px solid var(--color-warning);
`;

const noteTitleStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--gl-yellow-dark);
`;

const noteTextStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const errorStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-danger);
`;

const footerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--space-sm);
`;

export interface DeleteBUModalProps {
  open: boolean;
  tenant: Tenant;
  isDeleting: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (tenant: Tenant) => void;
}

export const DeleteBUModal = ({
  open,
  tenant,
  isDeleting,
  error,
  onClose,
  onConfirm,
}: DeleteBUModalProps): JSX.Element => {
  const [typedSlug, setTypedSlug] = useState('');

  // Clear between openings so a previous confirmation never carries over.
  useEffect(() => {
    if (open) setTypedSlug('');
  }, [open, tenant.slug]);

  const preview = useQuery({
    queryKey: ['tenant-cascade-preview', tenant.slug],
    queryFn: () => getTenantCascadePreview(tenant.slug),
    enabled: open,
  });

  const canDelete = typedSlug.trim() === tenant.slug;

  const content = (
    <div className={bodyStyle}>
      <p className={textStyle}>
        A BU <span className={slugStyle}>{tenant.slug}</span> e tudo que está abaixo dela deixam de
        aparecer nos outros módulos. Isto é o que sai de circulação junto:
      </p>

      <CascadeSummary
        preview={preview.data}
        isLoading={preview.isLoading}
        error={preview.error?.message ?? null}
      />

      <div className={noteStyle}>
        <span className={noteTitleStyle}>Nada é apagado de verdade</span>
        <span className={noteTextStyle}>
          A exclusão é reversível: a BU vai para a aba Excluídas e cada item guarda o estado que
          tinha. Ao reativar, o que estava desligado antes continua desligado.
        </span>
      </div>

      <UTTextField
        id='delete-bu-confirm'
        topLabel={`Digite ${tenant.slug} para confirmar`}
        value={typedSlug}
        placeholder={tenant.slug}
        onChange={event => {
          setTypedSlug(event.target.value);
        }}
      />

      {error !== null ? <span className={errorStyle}>{error}</span> : null}

      <div className={footerStyle}>
        <UTButton fitContent variant='tertiary' text='Cancelar' onClick={onClose} />
        <UTButton
          fitContent
          id='delete-bu-confirm-button'
          variant='primary'
          text={isDeleting ? 'Excluindo...' : 'Excluir BU'}
          disabled={!canDelete || isDeleting}
          onClick={() => {
            onConfirm(tenant);
          }}
        />
      </div>
    </div>
  );

  return (
    <UTModal
      open={open}
      onClose={onClose}
      title='Excluir business unit'
      content={content}
      maxWidth='560px'
      hasButtonClose
      position='center'
    />
  );
};
